import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InvoiceScore, InvoiceScoreBundle, ScoreGrade } from '../../types/models';
import ScoreBadge from './ScoreBadge';
import apiService from '../../services/api';

interface ScoreBreakdownProps {
  invoiceId: number;
  /** Optional pre-loaded bundle; if absent the component fetches on first expand */
  bundle?: InvoiceScoreBundle | null;
  /** Notify parent when the bundle is (re)loaded internally */
  onBundleLoaded?: (bundle: InvoiceScoreBundle) => void;
}

const FLAG_LABELS: Record<string, string> = {
  first_time_debtor:    'Deudor sin historial',
  large_amount:         'Monto inusualmente alto',
  near_due:             'Vence pronto',
  incomplete_documents: 'Documentos incompletos',
  new_seller:           'Vendedor nuevo',
};

const FLAG_SEVERITY: Record<string, 'warning' | 'error' | 'default'> = {
  first_time_debtor:    'warning',
  large_amount:         'warning',
  near_due:             'error',
  incomplete_documents: 'error',
  new_seller:           'default',
};

const SUB_SCORES = [
  { key: 'debtor_score'          as keyof InvoiceScore, label: 'Deudor',           weight: '35%' },
  { key: 'invoice_quality_score' as keyof InvoiceScore, label: 'Calidad',           weight: '25%' },
  { key: 'seller_score'          as keyof InvoiceScore, label: 'Vendedor',          weight: '25%' },
  { key: 'market_score'          as keyof InvoiceScore, label: 'Contexto Mercado',  weight: '15%' },
];

const barColor = (value: number) => {
  if (value >= 70) return '#2e7d32';
  if (value >= 50) return '#f57c00';
  return '#d32f2f';
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({
  invoiceId,
  bundle: preloadedBundle,
  onBundleLoaded,
}) => {
  const [bundle, setBundle] = useState<InvoiceScoreBundle | null>(preloadedBundle ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (preloadedBundle !== undefined) {
      setBundle(preloadedBundle);
      return;
    }
    if (!expanded || bundle) return;
    setLoading(true);
    setError(null);
    apiService
      .get<InvoiceScoreBundle>(`/invoices/${invoiceId}/score`)
      .then((data) => {
        setBundle(data);
        setLoading(false);
        onBundleLoaded?.(data);
      })
      .catch(() => {
        setError('Puntaje no disponible para esta factura');
        setLoading(false);
      });
  }, [expanded, invoiceId, preloadedBundle, bundle, onBundleLoaded]);

  const auto: InvoiceScore | null = bundle?.auto ?? null;
  const override = bundle?.override ?? null;
  const effectiveGrade = bundle?.effective_grade ?? null;
  const effectiveScore = bundle?.effective_score ?? null;
  const isOverridden = bundle?.source === 'override';

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Análisis de Riesgo
          </Typography>
          {effectiveGrade && (
            <ScoreBadge
              grade={effectiveGrade as ScoreGrade}
              score={effectiveScore ?? undefined}
              confidence={auto && !isOverridden ? (auto.confidence as any) : undefined}
              size="small"
              overridden={isOverridden}
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
        {!loading && !error && !bundle?.auto && !bundle?.override && (
          <Alert severity="info">Aún no se ha calculado el rating de esta factura.</Alert>
        )}

        {bundle && (
          <Stack spacing={2}>
            {override && (
              <Alert severity="info" icon={false} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Rating modificado por administrador
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {override.score.toFixed(1)} · {override.grade}
                  {override.by_name ? ` · ${override.by_name}` : override.by_email ? ` · ${override.by_email}` : ''}
                  {override.at ? ` · ${formatDateTime(override.at)}` : ''}
                </Typography>
                {override.reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    "{override.reason}"
                  </Typography>
                )}
              </Alert>
            )}

            {auto && (
              <>
                {override && (
                  <Typography variant="caption" color="text.secondary">
                    Cálculo automático (referencia): {auto.score.toFixed(1)} · {auto.grade}
                  </Typography>
                )}
                {SUB_SCORES.map(({ key, label, weight }) => {
                  const value = auto[key] as number;
                  return (
                    <Box key={key as string}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {label}{' '}
                          <Typography component="span" variant="caption" color="text.disabled">
                            ({weight})
                          </Typography>
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {value.toFixed(0)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={value}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: barColor(value),
                          },
                        }}
                      />
                    </Box>
                  );
                })}

                {auto.risk_flags && auto.risk_flags.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Factores de Riesgo
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {auto.risk_flags.map((flag) => (
                          <Chip
                            key={flag}
                            label={FLAG_LABELS[flag] ?? flag}
                            size="small"
                            color={FLAG_SEVERITY[flag] ?? 'default'}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  </>
                )}

                <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right' }}>
                  Confianza: {auto.confidence} · v{auto.version}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ScoreBreakdown;
